import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Send, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import DepartmentSelect from './DepartmentSelect';
import { STANDARD_DEPARTMENTS } from '../data/departments';

/**
 * Guided "first pulse" launcher used as Driver.js tour anchors on the dashboard.
 */
export default function FirstPulsePanel({ departments = [], unlocked = false }) {
  const navigate = useNavigate();
  const [team, setTeam] = useState(STANDARD_DEPARTMENTS[0]);
  const [frequency, setFrequency] = useState('quarterly');

  useEffect(() => {
    const first = departments?.[0]?.name;
    if (first) setTeam(first);
  }, [departments]);

  return (
    <div
      className="relative mb-8 rounded-3xl border border-teal-200/60 bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950 p-6 md:p-8 text-white shadow-[0_0_40px_-12px_rgba(13,148,136,0.45)] overflow-hidden"
      data-testid="first-pulse-panel"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            'radial-gradient(ellipse at 20% 0%, rgba(45,212,191,0.25) 0%, transparent 50%), radial-gradient(ellipse at 90% 80%, rgba(34,211,238,0.12) 0%, transparent 45%)',
        }}
      />

      <div className="relative z-10">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-teal-300 mb-2">
              First Pulse Setup
            </p>
            <h2 className="font-heading text-xl md:text-2xl font-semibold text-white tracking-tight">
              Launch your anonymous employee pulse
            </h2>
            <p className="text-sm text-slate-400 mt-1.5 max-w-xl">
              Walk through team selection, privacy settings, and launch — designed for your first survey in under a minute.
            </p>
          </div>
          <button
            type="button"
            data-tour="tour-create-survey"
            className="inline-flex items-center gap-2 shrink-0 rounded-xl border border-teal-400/40 bg-teal-500/15 px-4 py-2.5 text-sm font-semibold text-teal-100 hover:bg-teal-500/25 hover:border-teal-300/60 transition-colors shadow-[0_0_20px_rgba(20,184,166,0.25)]"
          >
            <Sparkles className="w-4 h-4 text-teal-300" />
            Create Survey / New Pulse
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <div
            data-tour="tour-team-select"
            className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4"
          >
            <DepartmentSelect
              id="tour-team"
              label="Target team / department"
              value={team}
              onChange={setTeam}
              companyDepartments={departments}
              dark
              required
              data-testid="tour-team-select"
            />
            <p className="text-[11px] text-slate-500 mt-2">
              Pick a standard team or choose Other / Custom.
            </p>
          </div>

          <div
            data-tour="tour-anonymity-settings"
            className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md p-4 md:col-span-2"
          >
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
              Anonymity &amp; frequency
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 flex items-start gap-3 rounded-xl border border-teal-400/25 bg-teal-500/10 px-3 py-2.5">
                <ShieldCheck className="w-5 h-5 text-teal-300 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-teal-100">Strict anonymity on</p>
                  <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                    No email IDs or PII are ever shown to HR. Insights unlock at team level only.
                  </p>
                </div>
              </div>
              <div className="sm:w-44">
                <label htmlFor="tour-frequency" className="block text-[11px] font-medium text-slate-400 mb-1.5">
                  Pulse frequency
                </label>
                <select
                  id="tour-frequency"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                  className="w-full rounded-xl bg-slate-950/60 border border-white/15 text-slate-100 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                >
                  <option value="quarterly">Quarterly</option>
                  <option value="monthly">Monthly</option>
                  <option value="once">One-time</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Button
            data-tour="tour-launch-cta"
            onClick={() =>
              navigate('/surveys', {
                state: { preferredDepartment: team, frequency },
              })
            }
            disabled={!unlocked}
            className="h-11 px-6 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold shadow-[0_0_28px_rgba(20,184,166,0.45)] disabled:opacity-50"
          >
            <Send className="w-4 h-4 mr-2" />
            Generate &amp; Launch Survey
          </Button>
          {!unlocked && (
            <p className="text-xs text-slate-500">
              Unlock a plan to send invites — you can still preview this setup now.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
